import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "lucide-react";

export type DateRangePreset =
  | "7d"
  | "14d"
  | "30d"
  | "90d"
  | "6mo"
  | "ytd"
  | "all-time"
  | "custom";

interface DateRangePickerProps {
  value: number;
  onChange: (days: number, preset: DateRangePreset) => void;
  className?: string;
}

function calculateDateRangeDays(preset: DateRangePreset): number {
  const now = new Date();
  switch (preset) {
    case "7d":
      return 7;
    case "14d":
      return 14;
    case "30d":
      return 30;
    case "90d":
      return 90;
    case "6mo":
      return 180;
    case "ytd": {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return Math.ceil(
        (now.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24),
      );
    }
    case "all-time":
      return 36500; // 100 years
    default:
      return 30;
  }
}

function getPresetLabel(preset: DateRangePreset): string {
  switch (preset) {
    case "7d":
      return "7 Days";
    case "14d":
      return "14 Days";
    case "30d":
      return "30 Days";
    case "90d":
      return "90 Days";
    case "6mo":
      return "6 Months";
    case "ytd":
      return "Year to Date";
    case "all-time":
      return "All Time";
    case "custom":
      return "Custom";
    default:
      return "30 Days";
  }
}

export function DateRangePicker({
  value,
  onChange,
  className = "",
}: DateRangePickerProps) {
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>("30d");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  const presets: DateRangePreset[] = ["7d", "14d", "30d", "90d", "6mo", "ytd", "all-time"];

  const handlePresetClick = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
    const days = calculateDateRangeDays(preset);
    onChange(days, preset);
  };

  const handleCustomApply = () => {
    if (!customStartDate || !customEndDate) return;

    const start = new Date(customStartDate);
    const end = new Date(customEndDate);

    if (start > end) {
      alert("Start date must be before end date");
      return;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    setSelectedPreset("custom");
    onChange(diffDays, "custom");
    setIsCustomOpen(false);
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <Button
            key={preset}
            size="sm"
            variant={selectedPreset === preset ? "default" : "outline"}
            onClick={() => handlePresetClick(preset)}
          >
            {getPresetLabel(preset)}
          </Button>
        ))}

        <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant={selectedPreset === "custom" ? "default" : "outline"}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Custom
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-4">
              <h4 className="font-medium text-sm">Custom Date Range</h4>
              <div className="space-y-2">
                <div>
                  <Label htmlFor="start-date" className="text-sm">
                    Start Date
                  </Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || undefined}
                  />
                </div>
                <div>
                  <Label htmlFor="end-date" className="text-sm">
                    End Date
                  </Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate || undefined}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>
              <Button
                onClick={handleCustomApply}
                disabled={!customStartDate || !customEndDate}
                className="w-full"
              >
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing data from{" "}
        <span className="font-medium">
          {selectedPreset === "all-time"
            ? "all time"
            : `last ${value} days`}
        </span>
      </div>
    </div>
  );
}
